/**
 * Crew Dashboard
 *
 * Mobile-first home page showing:
 * - Upcoming confirmed shifts (first)
 * - Pending assignments needing response
 * - Unified events view (availability + pending combined)
 *
 * Uses React Query with polling for real-time updates.
 */

import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useCrewDashboard } from '../../hooks/queries';
import { DataFreshness } from '../../components/ui/DataFreshness';
import {
  setAvailability,
  type Availability,
  type AvailabilityStatus,
} from '../../api/availability';
import { respondToAssignment, type Assignment } from '../../api/assignments';
import { getErrorMessage } from '../../api/client';
import { AvailabilityBadge, AvailabilityNoteModal } from '../../components/availability';
import {
  startOfToday,
  compareByDateThenStart,
  formatDateWithWeekday,
  formatTimeWithZone,
} from '../../utils/dateFormatting';
import { Modal } from '../../components/ui';
import SmsOptInPrompt from '../../components/profile/SmsOptInPrompt';
import InstallPrompt from '../../components/pwa/InstallPrompt';
import EnablePushCard from '../../components/pwa/EnablePushCard';
import { isMobileDevice } from '../../utils/device';

// =============================================================================
// Types for unified events view
// =============================================================================

type UnifiedEventState =
  | { type: 'crewed'; assignment: Assignment }
  // The 'availability' state always has a chosen status — note-only rows
  // (status null) render as 'pending' instead, with the note carried in
  // myAvailability below.
  | { type: 'availability'; availability: Availability & { status: AvailabilityStatus } }
  | { type: 'pending' };

interface UnifiedEvent {
  id: string;
  name: string;
  venue: string;
  eventDate: string;
  callTime?: string | null;
  startTime?: string | null;
  sport?: string | null;
  state: UnifiedEventState;
  /** The user's availability row for this event (any state), for the note. */
  myAvailability?: Availability | null;
}

export default function CrewDashboard() {
  const { user } = useAuth();
  const {
    pendingEvents,
    myAvailability,
    myAssignments,
    loading,
    error,
    isRefetching,
    dataUpdatedAt,
    refetch,
  } = useCrewDashboard();

  // Show all events toggle (default: only show next 2 weeks)
  const [showAllEvents, setShowAllEvents] = useState(false);

  // Decline modal state
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [decliningAssignment, setDecliningAssignment] = useState<Assignment | null>(null);

  // Filter assignments - visible to crew when the event is PUBLISHED (or
  // already had its bulk-notify flag set via the legacy "Confirm & Notify Crew"
  // flow). Past events are hidden from the crew dashboard either way.
  // Events happening today still count as upcoming (compare to start of today,
  // not the current moment).
  const todayStart = startOfToday();
  const visibleAssignments = myAssignments.filter((a) => {
    if (!a.event) return false;
    const isVisible = a.event.status === 'PUBLISHED' || a.event.assignmentsConfirmed;
    return isVisible && new Date(a.event.eventDate) >= todayStart;
  });

  // Pending assignments need crew response (status = ASSIGNED)
  const pendingAssignments = visibleAssignments.filter((a) => a.status === 'ASSIGNED');

  // Confirmed assignments (for "Your Upcoming Shifts" section)
  const confirmedAssignments = visibleAssignments.filter((a) => a.status === 'CONFIRMED');

  // Build unified events list
  const unifiedEvents = useMemo(() => {
    const cutoff = startOfToday();
    const eventMap = new Map<string, UnifiedEvent>();

    // The user's availability row per event (any state) — carries the note
    // onto every card variant.
    const availByEventId = new Map(myAvailability.map((a) => [a.eventId, a]));

    // Add events where user is crewed (confirmed assignments)
    for (const assignment of confirmedAssignments) {
      if (!assignment.event) continue;
      eventMap.set(assignment.event.id, {
        id: assignment.event.id,
        name: assignment.event.name,
        venue: assignment.event.venue,
        eventDate: assignment.event.eventDate,
        callTime: assignment.event.callTime,
        startTime: assignment.event.startTime,
        sport: assignment.event.sport,
        state: { type: 'crewed', assignment },
        myAvailability: availByEventId.get(assignment.event.id) ?? null,
      });
    }

    // Add events where user has set availability (but not crewed).
    // Note-only rows (status null) are skipped here — those events arrive via
    // pendingEvents below and must render as 'pending' (with quick-toggle
    // buttons), not as a status card.
    for (const availability of myAvailability) {
      const { status } = availability;
      if (!availability.event || status === null) continue;
      if (new Date(availability.event.eventDate) < cutoff) continue;
      if (eventMap.has(availability.event.id)) continue; // Already crewed

      eventMap.set(availability.event.id, {
        id: availability.event.id,
        name: availability.event.name,
        venue: availability.event.venue,
        eventDate: availability.event.eventDate,
        callTime: availability.event.callTime,
        startTime: availability.event.startTime,
        sport: availability.event.sport,
        state: { type: 'availability', availability: { ...availability, status } },
        myAvailability: availability,
      });
    }

    // Add pending events (no availability status set, not crewed)
    for (const event of pendingEvents) {
      if (eventMap.has(event.id)) continue; // Already has availability or crewed

      eventMap.set(event.id, {
        id: event.id,
        name: event.name,
        venue: event.venue,
        eventDate: event.eventDate,
        callTime: event.callTime,
        startTime: event.startTime,
        sport: event.sport,
        state: { type: 'pending' },
        myAvailability: availByEventId.get(event.id) ?? null,
      });
    }

    return Array.from(eventMap.values()).sort(compareByDateThenStart);
  }, [pendingEvents, myAvailability, confirmedAssignments]);

  // Filter unified events: by default only show events within next 2 weeks
  const twoWeeksFromNow = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 14);
    return date;
  }, []);

  const eventsWithin2Weeks = unifiedEvents.filter(
    (event) => new Date(event.eventDate) <= twoWeeksFromNow
  );
  const eventsBeyond2Weeks = unifiedEvents.filter(
    (event) => new Date(event.eventDate) > twoWeeksFromNow
  );

  const displayedEvents = showAllEvents ? unifiedEvents : eventsWithin2Weeks;

  // Count stats
  const upcomingShiftCount = confirmedAssignments.length;
  const pendingAssignmentCount = pendingAssignments.length;

  // Pending availability: events within 2 weeks that user hasn't set availability for
  const pendingAvailabilityWithin2Weeks = pendingEvents.filter(
    (event) => new Date(event.eventDate) <= twoWeeksFromNow
  );
  const pendingAvailabilityCount = pendingAvailabilityWithin2Weeks.length;

  const handleOpenDeclineModal = (assignment: Assignment) => {
    setDecliningAssignment(assignment);
    setDeclineModalOpen(true);
  };

  const handleCloseDeclineModal = () => {
    setDeclineModalOpen(false);
    setDecliningAssignment(null);
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-gray-200 w-1/3"></div>
        <div className="grid grid-cols-3 gap-3">
          <div className="h-20 bg-gray-200"></div>
          <div className="h-20 bg-gray-200"></div>
          <div className="h-20 bg-gray-200"></div>
        </div>
        <div className="h-32 bg-gray-200"></div>
        <div className="h-32 bg-gray-200"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* PWA install + device-push nudges — phones/tablets only. On a desktop
          these read as noise stacked on top of the SMS card; a desktop user
          who wants them still finds both on their Profile page. Cards also
          self-hide when installed/enabled/dismissed. */}
      {isMobileDevice() && (
        <>
          <InstallPrompt />
          <EnablePushCard />
        </>
      )}

      {/* Welcome header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-heading text-ink">
            Hey, {user?.firstName}!
          </h1>
          <p className="text-ink-body text-sm mt-1">
            Here's what's coming up for you.
          </p>
        </div>
        <DataFreshness
          dataUpdatedAt={dataUpdatedAt}
          onRefresh={refetch}
          isRefreshing={isRefetching}
        />
      </div>

      {/* Error banner */}
      {error && (
        <div className="p-4 bg-white border border-hairline border-l-[3px] border-l-program text-program-dark text-sm">
          {error}
        </div>
      )}

      {/* SMS opt-in nudge. Renders nothing once the user opts in, opts out, or
          dismisses it — consent has to be self-service, so this is the only way
          anyone discovers the setting. */}
      <SmsOptInPrompt userId={user?.id} />

      {/* Quick stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-hairline border-t-[3px] border-t-rack p-4">
          <p className="text-[30px] leading-none font-semibold text-ink">{upcomingShiftCount}</p>
          <p className="text-xs text-ink-muted mt-2">Confirmed</p>
        </div>
        <div className="bg-white border border-hairline border-t-[3px] border-t-rack p-4">
          <p className="text-[30px] leading-none font-semibold text-ink">{pendingAssignmentCount}</p>
          <p className="text-xs text-ink-muted mt-2">Confirm shifts</p>
        </div>
        <div className="bg-white border border-hairline border-t-[3px] border-t-rack p-4">
          <p className="text-[30px] leading-none font-semibold text-ink">{pendingAvailabilityCount}</p>
          <p className="text-xs text-ink-muted mt-2">Set availability</p>
        </div>
      </div>

      {/* Your Upcoming Shifts - FIRST */}
      <section>
        <h2 className="text-lg font-semibold text-ink mb-3">
          Your Upcoming Shifts
        </h2>
        {confirmedAssignments.length > 0 ? (
          <div className="space-y-3">
            {confirmedAssignments.slice(0, 5).map((assignment) => (
              <ConfirmedAssignmentCard key={assignment.id} assignment={assignment} />
            ))}
          </div>
        ) : (
          <div className="bg-white border border-hairline p-6 text-center">
            <p className="text-ink-muted">No confirmed shifts yet</p>
          </div>
        )}
      </section>

      {/* Pending Assignments - Need Response */}
      {pendingAssignments.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-ink mb-3">
            Confirm Your Assignments
          </h2>
          <div className="space-y-3">
            {pendingAssignments.map((assignment) => (
              <PendingAssignmentCard
                key={assignment.id}
                assignment={assignment}
                onResponse={refetch}
                onDecline={() => handleOpenDeclineModal(assignment)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Unified Events Section - Combined Availability View */}
      {unifiedEvents.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-ink">
              Upcoming Events
            </h2>
            {!showAllEvents && eventsBeyond2Weeks.length > 0 && (
              <span className="text-xs text-ink-muted">
                Next 2 weeks
              </span>
            )}
          </div>

          {displayedEvents.length > 0 ? (
            <div className="space-y-3">
              {displayedEvents.map((event) => (
                <UnifiedEventCard
                  key={event.id}
                  event={event}
                  onResponse={refetch}
                />
              ))}
            </div>
          ) : (
            <div className="bg-white border border-hairline p-4 text-center">
              <p className="text-ink-muted text-sm">No events in the next 2 weeks</p>
            </div>
          )}

          {/* Show more/less button if there are events beyond 2 weeks */}
          {eventsBeyond2Weeks.length > 0 && (
            <button
              onClick={() => setShowAllEvents(!showAllEvents)}
              className="mt-3 w-full py-2 text-sm font-medium text-ink border border-hairline bg-white hover:bg-rack hover:text-white hover:border-rack transition-colors"
            >
              {showAllEvents
                ? 'Show only next 2 weeks'
                : `Show all events (${eventsBeyond2Weeks.length} more)`}
            </button>
          )}
        </section>
      )}

      {/* Decline Modal */}
      {decliningAssignment && (
        <DeclineModal
          isOpen={declineModalOpen}
          onClose={handleCloseDeclineModal}
          assignment={decliningAssignment}
          onDeclined={refetch}
        />
      )}
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

interface PendingAssignmentCardProps {
  assignment: Assignment;
  onResponse: () => void;
  onDecline: () => void;
}

function PendingAssignmentCard({ assignment, onResponse, onDecline }: PendingAssignmentCardProps) {
  const [isConfirming, setIsConfirming] = useState(false);
  const event = assignment.event;
  const role = assignment.role;

  if (!event) return null;

  const formattedDate = formatDateWithWeekday(event.eventDate);
  const formattedCallTime = event.callTime
    ? formatTimeWithZone(event.callTime)
    : null;

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await respondToAssignment(assignment.id, { status: 'CONFIRMED' });
      onResponse();
      toast.success('Assignment confirmed');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className="bg-white border border-hairline border-l-[3px] border-l-program p-4">
      <div className="flex justify-between items-start gap-2">
        <div className="min-w-0">
          <Link to={`/crew/events/${event.id}`} className="font-medium text-ink hover:text-program">
            {event.name}
          </Link>
          <p className="text-sm text-ink-muted">{event.venue}</p>
        </div>
        <span className="bg-rack text-white text-xs font-medium px-2 py-1 whitespace-nowrap">
          {role?.roleName || 'TBD'}
        </span>
      </div>
      <div className="mt-2 flex items-center text-sm text-ink-body">
        <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        {formattedDate}
      </div>
      {formattedCallTime && (
        <div className="mt-1 flex items-center text-[15px] font-semibold text-ink">
          <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Crew Call: {formattedCallTime}
        </div>
      )}
      <div className="mt-2 flex justify-end">
        <Link to={`/crew/events/${event.id}`} className="text-xs text-ink-body hover:text-ink">
          View details →
        </Link>
      </div>
      {/* Confirm is the committed action and reads solid; Decline is outlined
          and inverts on press. Both clear a 44px touch target. */}
      <div className="mt-3 flex gap-2">
        <button
          onClick={handleConfirm}
          disabled={isConfirming}
          className="flex-1 min-h-[44px] bg-rack text-white text-sm font-medium hover:bg-rack-hairline active:bg-rack-deep disabled:opacity-50"
        >
          {isConfirming ? 'Confirming...' : 'Confirm'}
        </button>
        <button
          onClick={onDecline}
          disabled={isConfirming}
          className="flex-1 min-h-[44px] border border-hairline bg-white text-ink text-sm font-medium hover:bg-rack hover:text-white hover:border-rack disabled:opacity-50"
        >
          Decline
        </button>
      </div>
    </div>
  );
}

interface ConfirmedAssignmentCardProps {
  assignment: Assignment;
}

function ConfirmedAssignmentCard({ assignment }: ConfirmedAssignmentCardProps) {
  const event = assignment.event;
  const role = assignment.role;

  if (!event) return null;

  const formattedDate = formatDateWithWeekday(event.eventDate);
  const formattedCallTime = event.callTime
    ? formatTimeWithZone(event.callTime)
    : null;

  return (
    <Link
      to={`/crew/events/${event.id}`}
      className="block bg-white border border-hairline border-l-[3px] border-l-preview p-4 hover:bg-surface-hover transition-colors"
    >
      <div className="flex justify-between items-start gap-2">
        <div className="min-w-0">
          <h3 className="font-medium text-ink">{event.name}</h3>
          <p className="text-sm text-ink-muted">{event.venue}</p>
        </div>
        <span className="bg-rack text-white text-xs font-medium px-2 py-1 whitespace-nowrap">
          {role?.roleName || 'TBD'}
        </span>
      </div>
      <div className="mt-2 flex items-center text-sm text-ink-body">
        <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        {formattedDate}
      </div>
      {formattedCallTime && (
        <div className="mt-1 flex items-center text-[15px] font-semibold text-ink">
          <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Crew Call: {formattedCallTime}
        </div>
      )}
      <div className="mt-2 flex items-center justify-between">
        <div className="flex items-center text-xs text-preview-text">
          <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Confirmed
        </div>
        <span className="text-xs text-ink-muted">View details →</span>
      </div>
    </Link>
  );
}

// =============================================================================
// Availability action buttons
// =============================================================================

/**
 * Three equal outlined buttons, one per answer, each carrying its own meaning
 * colour on the border only. Amber (not red) stays on Maybe: it is a hedge,
 * not a refusal. Minimum 44px tall — this is the control crew actually tap,
 * usually one-handed, often outdoors.
 */
const AVAILABILITY_BUTTON_BASE =
  'flex-1 min-h-[44px] px-2 bg-white text-sm font-medium border transition-colors disabled:opacity-50';

const AVAILABILITY_BUTTON: Record<AvailabilityStatus, string> = {
  AVAILABLE: `${AVAILABILITY_BUTTON_BASE} border-preview text-preview-text hover:bg-preview hover:text-white`,
  MAYBE: `${AVAILABILITY_BUTTON_BASE} border-maybe text-maybe-dark hover:bg-maybe hover:text-white`,
  NOT_AVAILABLE: `${AVAILABILITY_BUTTON_BASE} border-program text-program-dark hover:bg-program hover:text-white`,
};

/** Same buttons in edit mode, with the current answer shown filled. */
function availabilityButtonClass(
  option: AvailabilityStatus,
  current: AvailabilityStatus
): string {
  if (option !== current) return AVAILABILITY_BUTTON[option];
  const fill = {
    AVAILABLE: 'border-preview bg-preview text-white',
    MAYBE: 'border-maybe bg-maybe text-white',
    NOT_AVAILABLE: 'border-program bg-program text-white',
  }[option];
  return `${AVAILABILITY_BUTTON_BASE} ${fill}`;
}

// =============================================================================
// Unified Event Card - Shows crewed, availability status, or pending
// =============================================================================

interface UnifiedEventCardProps {
  event: UnifiedEvent;
  onResponse: () => void;
}

function UnifiedEventCard({ event, onResponse }: UnifiedEventCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<AvailabilityStatus | null>(null);
  const [noteModalOpen, setNoteModalOpen] = useState(false);

  const myNote = event.myAvailability?.notes ?? null;

  const formattedDate = formatDateWithWeekday(event.eventDate);
  const formattedStartTime = event.startTime
    ? formatTimeWithZone(event.startTime)
    : null;
  const formattedCallTime = event.callTime
    ? formatTimeWithZone(event.callTime)
    : null;

  const handleSetAvailability = async (status: AvailabilityStatus) => {
    setIsSubmitting(status);
    try {
      await setAvailability(event.id, { status });
      onResponse();
      setIsEditing(false);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setIsSubmitting(null);
    }
  };

  // The left rail says what this card wants from you: green = you are crewed,
  // red = it is waiting on your answer, gray = answered, nothing to do.
  const getRailClass = () => {
    if (event.state.type === 'crewed') return 'border-l-preview';
    if (event.state.type === 'pending') return 'border-l-program';
    return 'border-l-hairline';
  };

  return (
    <div className={`bg-white border border-hairline border-l-[3px] ${getRailClass()} p-4`}>
      <div className="flex justify-between items-start gap-2">
        <div className="min-w-0">
          <Link to={`/crew/events/${event.id}`} className="font-medium text-ink hover:text-program">
            {event.name}
          </Link>
          <p className="text-sm text-ink-muted">
            {event.venue} • {formattedDate}{formattedStartTime ? ` at ${formattedStartTime}` : ''}
          </p>
          {formattedCallTime && (
            <p className="text-[15px] font-semibold text-ink mt-1">
              Crew Call: {formattedCallTime}
            </p>
          )}
          {event.sport && (
            <p className="text-xs text-ink-muted mt-1">{event.sport}</p>
          )}
        </div>

        {/* Status badge */}
        <div className="flex items-center gap-2">
          {event.state.type === 'crewed' && (
            <span className="border border-preview bg-white text-preview-text text-xs font-medium px-2 py-1 flex items-center gap-1 whitespace-nowrap">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Crewed
            </span>
          )}
          {event.state.type === 'availability' && (
            <>
              <AvailabilityBadge status={event.state.availability.status} />
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="text-ink-muted hover:text-ink"
                title="Change availability"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            </>
          )}
          {event.state.type === 'pending' && (
            <span className="border border-maybe bg-white text-maybe-dark text-xs font-medium px-2 py-1 whitespace-nowrap">
              Pending
            </span>
          )}
        </div>
      </div>

      {/* Show role if crewed */}
      {event.state.type === 'crewed' && event.state.assignment.role && (
        <div className="mt-2 flex items-center justify-between">
          <p className="text-sm text-ink-body">
            Role: {event.state.assignment.role.roleName}
          </p>
          <Link to={`/crew/events/${event.id}`} className="text-xs text-ink-body hover:text-ink">
            View details →
          </Link>
        </div>
      )}

      {/* Availability buttons for pending events */}
      {event.state.type === 'pending' && (
        <div className="mt-3 flex gap-2">
          <button
            onClick={() => handleSetAvailability('AVAILABLE')}
            disabled={isSubmitting !== null}
            className={AVAILABILITY_BUTTON.AVAILABLE}
          >
            {isSubmitting === 'AVAILABLE' ? '...' : 'Available'}
          </button>
          <button
            onClick={() => handleSetAvailability('NOT_AVAILABLE')}
            disabled={isSubmitting !== null}
            className={AVAILABILITY_BUTTON.NOT_AVAILABLE}
          >
            {isSubmitting === 'NOT_AVAILABLE' ? '...' : 'Unavailable'}
          </button>
          <button
            onClick={() => handleSetAvailability('MAYBE')}
            disabled={isSubmitting !== null}
            className={AVAILABILITY_BUTTON.MAYBE}
          >
            {isSubmitting === 'MAYBE' ? '...' : 'Maybe'}
          </button>
        </div>
      )}

      {/* Edit mode for existing availability */}
      {event.state.type === 'availability' && isEditing && (
        <div className="mt-3 pt-3 border-t border-hairline-light">
          <p className="text-xs text-ink-muted mb-2">Change your availability:</p>
          <div className="flex gap-2">
            <button
              onClick={() => handleSetAvailability('AVAILABLE')}
              disabled={isSubmitting !== null}
              className={availabilityButtonClass('AVAILABLE', event.state.availability.status)}
            >
              {isSubmitting === 'AVAILABLE' ? '...' : 'Available'}
            </button>
            <button
              onClick={() => handleSetAvailability('MAYBE')}
              disabled={isSubmitting !== null}
              className={availabilityButtonClass('MAYBE', event.state.availability.status)}
            >
              {isSubmitting === 'MAYBE' ? '...' : 'Maybe'}
            </button>
            <button
              onClick={() => handleSetAvailability('NOT_AVAILABLE')}
              disabled={isSubmitting !== null}
              className={availabilityButtonClass('NOT_AVAILABLE', event.state.availability.status)}
            >
              {isSubmitting === 'NOT_AVAILABLE' ? '...' : 'Unavailable'}
            </button>
          </div>
          <button
            onClick={() => setIsEditing(false)}
            className="mt-2 w-full text-xs text-ink-muted hover:text-ink"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Availability note — always editable, before or after picking a status */}
      <div className="mt-2 flex items-start justify-between gap-2">
        {myNote ? (
          <p className="text-xs text-ink-muted italic line-clamp-2 flex-1" title={myNote}>
            Note: {myNote}
          </p>
        ) : (
          <span className="flex-1" />
        )}
        <button
          onClick={() => setNoteModalOpen(true)}
          className="text-xs text-ink-body hover:text-ink underline whitespace-nowrap"
        >
          {myNote ? 'Edit Note' : 'Add Note'}
        </button>
      </div>
      {noteModalOpen && (
        <AvailabilityNoteModal
          isOpen
          onClose={() => setNoteModalOpen(false)}
          onSuccess={onResponse}
          eventId={event.id}
          eventName={event.name}
          currentNote={myNote}
        />
      )}
    </div>
  );
}

// =============================================================================
// Decline Modal
// =============================================================================

interface DeclineModalProps {
  isOpen: boolean;
  onClose: () => void;
  assignment: Assignment;
  onDeclined: () => void;
}

function DeclineModal({ isOpen, onClose, assignment, onDeclined }: DeclineModalProps) {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDecline = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await respondToAssignment(assignment.id, {
        status: 'DECLINED',
        declineReason: reason.trim() || null,
      });
      onDeclined();
      onClose();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" labelledBy="decline-assignment-title">
      <div className="px-6 py-4 border-b border-hairline">
        <h3 id="decline-assignment-title" className="text-lg font-semibold text-ink">Decline Assignment</h3>
        <p className="text-sm text-ink-muted mt-1">
          {assignment.event?.name} - {assignment.role?.roleName}
        </p>
      </div>

      <div className="px-6 py-4">
        {error && (
          <div className="mb-4 p-3 bg-white border border-hairline border-l-[3px] border-l-program text-sm text-program-dark">
            {error}
          </div>
        )}

        <label htmlFor="reason" className="block text-sm font-medium text-ink-body mb-1">
          Reason (optional)
        </label>
        <textarea
          id="reason"
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={500}
          className="w-full px-3 py-2 border border-hairline focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack"
          placeholder="Let the admin know why you can't work this shift..."
        />
        <p className="text-xs text-ink-muted mt-1">{reason.length}/500 characters</p>
      </div>

      <div className="px-6 py-4 bg-surface-panel border-t border-hairline flex justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
          className="px-4 py-2 text-sm font-medium text-ink-body hover:bg-paper transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleDecline}
          disabled={isSubmitting}
          className="px-4 py-2 text-sm font-medium text-white bg-program hover:bg-program-dark transition-colors disabled:opacity-50"
        >
          {isSubmitting ? 'Declining...' : 'Decline Assignment'}
        </button>
      </div>
    </Modal>
  );
}
